import { selectHasMetadata } from '@va/dashboard/selectors/core';
import {
  useBrowserNameFilter,
  useBrowserVersionFilter,
  useCitiesFilter,
  useCompanyOrgNameFilter,
  useCompanyOrgTypeFilter,
  useCountriesFilter,
  useDeviceTypeFilter,
  useDisplayFilter,
  useIpFilter,
  useLandingPageUrlFilter,
  useNewReferrerFilter,
  usePageUrlFilter,
  usePlatformFilter,
  useTrafficChannelFilter,
  useUserAgentFilter,
  useUtmParametersFilters,
  useVisitMetadataFilters,
} from '@va/dashboard/shared/filters';
import { useTranslate } from '@va/localization';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export const useSessionMetricsFilters = () => {
  const translate = useTranslate();

  const hasMetadata = useSelector(selectHasMetadata);
  const { nestedFiltersPage } = useVisitMetadataFilters();

  const ipFilter = useIpFilter();
  const osFilter = usePlatformFilter();
  const deviceTypeFilter = useDeviceTypeFilter();
  const displayResolutionFilter = useDisplayFilter();
  const userAgentFilter = useUserAgentFilter();

  const countryFilter = useCountriesFilter();

  const browserNameFilter = useBrowserNameFilter();
  const browserVersionFilter = useBrowserVersionFilter();

  const companyOrgNameFilter = useCompanyOrgNameFilter();
  const companyOrgTypeFilter = useCompanyOrgTypeFilter();

  const cityFilter = useCitiesFilter({
    valueMapper: (value) => {
      const val = Array.isArray(value) ? value[0] : value;

      // Handle search for 'unknown' city in different languages
      if (typeof val === 'string' && val.toLowerCase().trim() === translate('labels.unknown').toLowerCase().trim())
        return [''];

      return value;
    },
  });

  const pageUrlFilter = usePageUrlFilter({ allowMultiSelect: true });
  const landingPageFilter = useLandingPageUrlFilter();
  const referrerUrlFilter = useNewReferrerFilter();

  const utmParametersFilters = useUtmParametersFilters();
  const trafficChannelFilters = useTrafficChannelFilter();

  const allFilters = useMemo(() => {
    const list = [
      ipFilter,
      osFilter,
      deviceTypeFilter,
      displayResolutionFilter,
      cityFilter,
      countryFilter,
      browserNameFilter,
      browserVersionFilter,
      companyOrgNameFilter,
      companyOrgTypeFilter,
      utmParametersFilters,
      pageUrlFilter,
      trafficChannelFilters,
      referrerUrlFilter,
      userAgentFilter,
      landingPageFilter,
    ];

    if (hasMetadata) {
      list.push(nestedFiltersPage);
    }

    return list;
  }, [
    ipFilter,
    osFilter,
    deviceTypeFilter,
    displayResolutionFilter,
    cityFilter,
    countryFilter,
    browserNameFilter,
    browserVersionFilter,
    companyOrgNameFilter,
    companyOrgTypeFilter,
    utmParametersFilters,
    pageUrlFilter,
    trafficChannelFilters,
    referrerUrlFilter,
    userAgentFilter,
    landingPageFilter,
    hasMetadata,
    nestedFiltersPage,
  ]);

  return useMemo(
    () => ({
      ipFilter,
      osFilter,
      deviceTypeFilter,
      displayResolutionFilter,
      userAgentFilter,
      countryFilter,
      browserNameFilter,
      browserVersionFilter,
      companyOrgNameFilter,
      companyOrgTypeFilter,
      cityFilter,
      pageUrlFilter,
      referrerUrlFilter,
      utmParametersFilters,
      trafficChannelFilters,
      landingPageFilter,
      allFilters,
    }),
    [
      allFilters,
      browserNameFilter,
      browserVersionFilter,
      cityFilter,
      companyOrgNameFilter,
      companyOrgTypeFilter,
      countryFilter,
      deviceTypeFilter,
      displayResolutionFilter,
      ipFilter,
      landingPageFilter,
      osFilter,
      pageUrlFilter,
      referrerUrlFilter,
      trafficChannelFilters,
      userAgentFilter,
      utmParametersFilters,
    ],
  );
};
